/**
 * useFileStatusActions — the File Detail status-transition flow
 * (decomposition step F5b, CLAUDE.md §8.4; the view-side enforcement
 * point of the §7.6 In Progress rules).
 *
 * Owns the state that only the status flow reads: the pause modal
 * (`showPauseModal`, `pauseReason`), the five pick-next states
 * (`showPickNextModal`, the two candidate lists, `pendingOutboundAction`,
 * `pendingPauseReason`), and the first-draw celebration video's deferred
 * return (`showFirstFileVideo`, `pendingReturnRef`, CLAUDE.md §22 / §39).
 * The three handlers — `handleStatusChange`, `handlePickNextFile`,
 * `handleCancelPickNext` — are the former FileDetail handlers verbatim
 * apart from the closure substitutions below; every service call, its
 * arguments, order, gate, note, return timing and error text is unchanged.
 *
 * Like F3's useHandoverTransition, the hook NEVER navigates and holds no
 * view state. Two callbacks carry the view's part of each transition:
 *
 *   onUpdated(updated, status)  — replaces the former tail `setFile`,
 *                                 `setNotesRefresh`, `setStatusAnim` and
 *                                 the `getAllFiles` refetch; awaited, so
 *                                 the refetch still precedes the return.
 *   onReturn(status)            — replaces `returnToDashboard(navigate,
 *                                 { role, cardType: 'file', cardId,
 *                                 message })`; the view builds the args
 *                                 from STATUS_LABELS, which stays there.
 *
 * The deferred return lives here: when the first-draw video will play,
 * `handleStatusChange` parks the status in `pendingReturnRef` instead of
 * calling `onReturn`, and `handleFirstFileVideoComplete` (the view passes
 * it as VideoInterstitial's onComplete) fires it — on natural completion
 * AND on the interstitial's error-path Continue button, so a failed video
 * never strands the page. `actionLoading` (read by the cancel guard),
 * `setActionLoading`, `setError` and `setConcurrencyWarning` stay view
 * state and arrive as inputs, as in F4.
 * The modal and video render sites stay in the view (DOM order) and read
 * the hook's return. The role × status gating lives in FileActionsCard.
 */

import { useState, useRef } from 'react';
import { getFirstName } from '../auth/authConfig';
import {
  drawFile,
  pauseFile,
  resumeFile,
  sendForSignature,
  markSigned,
  markCompleted,
  pauseAndPromote,
  sendForSignatureAndPromote,
} from '../services/pipelineService';
import { planNextFileAction } from '../utils/pipelineInvariants';

export default function useFileStatusActions({
  file,
  allFiles,
  user,
  role,
  actionLoading,
  setActionLoading,
  setError,
  setConcurrencyWarning,
  onUpdated,
  onReturn,
}) {
  const [pauseReason, setPauseReason] = useState('');
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showFirstFileVideo, setShowFirstFileVideo] = useState(false);
  // Return-to-dashboard (CLAUDE.md §39) deferred behind the first-draw
  // celebration video: the navigation args wait here until the interstitial
  // completes (or errors out through its Continue button), so the video is
  // never skipped and a failed video never strands the page.
  const pendingReturnRef = useRef(null);
  // Pick-next-file modal state (CLAUDE.md §7.6). Opens when the user
  // moves the In Progress file out (pause or sent_to_sign) AND other
  // paused files exist at that moment. The outbound action has NOT
  // committed yet — cancel is a true revert.
  const [showPickNextModal, setShowPickNextModal] = useState(false);
  const [pickNextPausedCandidates, setPickNextPausedCandidates] = useState([]);
  const [pickNextSubmittedCandidates, setPickNextSubmittedCandidates] = useState([]);
  // 'pause' | 'send_for_signature' — which outbound action triggered
  // the modal, so the picker knows which atomic helper to call.
  const [pendingOutboundAction, setPendingOutboundAction] = useState(null);
  const [pendingPauseReason, setPendingPauseReason] = useState('');

  // Status transition actions.
  //
  // All transitions delegate to the pipeline service (CLAUDE.md §7.6),
  // the single chokepoint that writes file.status + the matching
  // system note. The Pause and Sent-to-Sign flows are special:
  // they consult planNextFileAction(file, allFiles, …) and, if other
  // paused files exist, open the PickNextFileModal so the outbound +
  // inbound transitions commit atomically.
  const handleStatusChange = async (newStatus) => {
    try {
      setActionLoading(true);
      const currentFirstName = getFirstName(user?.email);
      const actor = { email: user?.email, id: user?.id, firstName: currentFirstName, role };

      let updated = file;
      // True when the first-draw celebration video will play: the return to
      // the dashboard then waits for the interstitial (§39).
      let deferReturn = false;

      switch (newStatus) {
        case 'in_progress':
          if (file.status === 'submitted') {
            updated = await drawFile(file, actor);
            // First-draw-of-the-month celebration video (assistant only).
            // Test records (CLAUDE.md §33) never trigger the celebration
            // and never count as the month's first draw.
            if (role === 'assistant' && !file.isTest) {
              const now = new Date();
              const currentMonth = now.getMonth();
              const currentYear = now.getFullYear();
              const hasDrawnThisMonth = allFiles.some((f) =>
                !f.isTest &&
                f.drawnBy === user?.email &&
                f.id !== file.id &&
                f.drawnAt &&
                new Date(f.drawnAt).getMonth() === currentMonth &&
                new Date(f.drawnAt).getFullYear() === currentYear
              );
              if (!hasDrawnThisMonth) {
                setShowFirstFileVideo(true);
                deferReturn = true;
              }
            }
          } else if (file.status === 'paused') {
            // Manual resume requires an empty In Progress slot — the
            // Resume button is disabled when canDrawFile is false, so
            // we should never get here with another active file.
            updated = await resumeFile(file, actor);
          }
          break;

        case 'paused': {
          // Test records (CLAUDE.md §33) are released from the In Progress
          // rules: pause commits directly — no forced-pick modal, no
          // "blocked" state. The pause itself (status write + system note
          // + reason note) uses the exact same pipelineService code path.
          if (file.isTest) {
            updated = await pauseFile(file, (pauseReason || '').trim(), actor);
            break;
          }
          const plan = planNextFileAction(file, allFiles, { action: 'pause' });
          if (plan.mode === 'blocked') {
            // Nothing to take over. Active file must stay In Progress —
            // surface the planner's message and abort without writing.
            setConcurrencyWarning(plan.message);
            setShowPauseModal(false);
            setPauseReason('');
            return;
          }
          // plan.mode === 'pick' — at least one candidate exists; the
          // user must pick (even with a single candidate). Defer the
          // actual pause until the modal commits, so cancelling is a
          // true no-op.
          setPendingPauseReason((pauseReason || '').trim());
          setPendingOutboundAction('pause');
          setPickNextPausedCandidates(plan.pausedCandidates);
          setPickNextSubmittedCandidates(plan.submittedCandidates);
          setShowPauseModal(false);
          setPauseReason('');
          setShowPickNextModal(true);
          return;
        }

        case 'sent_to_sign': {
          // Test records commit the send directly — no forced-pick modal
          // (CLAUDE.md §33). Same pipelineService path (system note +
          // attachment purge) as a real send.
          if (file.isTest) {
            updated = await sendForSignature(file, actor);
            break;
          }
          const plan = planNextFileAction(file, allFiles, {
            action: 'send_for_signature',
          });
          if (plan.mode === 'pick') {
            setPendingOutboundAction('send_for_signature');
            setPickNextPausedCandidates(plan.pausedCandidates);
            setPickNextSubmittedCandidates(plan.submittedCandidates);
            setShowPickNextModal(true);
            return;
          }
          // plan.mode === 'commit' — Paused is empty, so SfS may
          // legitimately leave In Progress empty.
          updated = await sendForSignature(file, actor);
          break;
        }

        case 'signed':
          updated = await markSigned(file, actor);
          break;

        case 'completed':
          updated = await markCompleted(file, actor);
          break;

        default:
          break;
      }

      setShowPauseModal(false);
      setPauseReason('');
      // The view applies the updated record, the notes refresh, the status
      // animation and the allFiles refetch (step F5b: handleStatusUpdated).
      await onUpdated(updated, newStatus);

      // Column changed and every write above succeeded: return to the
      // dashboard with this card highlighted in its new column (§39). The
      // early `return`s above (blocked pause, pick-next modal) never reach
      // here because nothing has been written yet.
      if (deferReturn) {
        pendingReturnRef.current = newStatus;
      } else {
        onReturn(newStatus);
      }
    } catch (err) {
      console.error('Status update failed:', err);
      setError('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  // Commit both the outbound action and the inbound promotion of the
  // picked file. Triggered by PickNextFileModal.onSelect once the
  // user has chosen the next file to work on. Routes to
  // pauseAndPromote / sendForSignatureAndPromote based on which
  // action originally opened the modal.
  const handlePickNextFile = async (picked /*, source */) => {
    try {
      setActionLoading(true);
      const currentFirstName = getFirstName(user?.email);
      const actor = { email: user?.email, id: user?.id, firstName: currentFirstName, role };

      let updated;
      if (pendingOutboundAction === 'pause') {
        const result = await pauseAndPromote({
          file,
          reason: pendingPauseReason,
          target: picked,
          actor,
        });
        updated = result.paused;
      } else {
        // 'send_for_signature'
        const result = await sendForSignatureAndPromote({
          file,
          target: picked,
          actor,
        });
        updated = result.sent;
      }

      setShowPickNextModal(false);
      setPickNextPausedCandidates([]);
      setPickNextSubmittedCandidates([]);
      setPendingPauseReason('');
      setPendingOutboundAction(null);

      // The view applies the updated record, the notes refresh, the status
      // animation (for the outbound status) and the allFiles refetch (step
      // F5b: handleStatusUpdated).
      const outboundStatus = pendingOutboundAction === 'pause' ? 'paused' : 'sent_to_sign';
      await onUpdated(updated, outboundStatus);

      // Outbound + inbound committed atomically: return to the dashboard
      // with THIS file (the one paused / sent) highlighted (§39).
      onReturn(outboundStatus);
    } catch (err) {
      console.error('Pick-next-file flow failed:', err);
      setError('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel = true revert of the outbound action. No DB writes have
  // happened yet; closing the modal leaves In Progress occupied with
  // the original file.
  const handleCancelPickNext = () => {
    if (actionLoading) return;
    setShowPickNextModal(false);
    setPickNextPausedCandidates([]);
    setPickNextSubmittedCandidates([]);
    setPendingPauseReason('');
    setPendingOutboundAction(null);
  };

  // First-draw celebration video: fires on natural completion AND on the
  // error path (the interstitial's "Continue to dashboard" button), so the
  // deferred return always runs. The view renders VideoInterstitial from
  // `showFirstFileVideo` and passes this as its onComplete.
  const handleFirstFileVideoComplete = () => {
    // Fires on natural completion AND on the error path (the
    // interstitial's "Continue to dashboard" button), so the
    // deferred return below always runs — a failed video never
    // strands the page (§39).
    setShowFirstFileVideo(false);
    const pending = pendingReturnRef.current;
    if (pending) {
      pendingReturnRef.current = null;
      onReturn(pending);
    }
  };

  return {
    handleStatusChange,
    handlePickNextFile,
    handleCancelPickNext,
    showPauseModal,
    setShowPauseModal,
    pauseReason,
    setPauseReason,
    showPickNextModal,
    pickNextPausedCandidates,
    pickNextSubmittedCandidates,
    pendingOutboundAction,
    showFirstFileVideo,
    handleFirstFileVideoComplete,
  };
}
